<script setup>
import { ref, computed, watch, onBeforeUnmount, nextTick, getCurrentInstance } from 'vue';
import CodemirrorEditor from '../../common/codemirror/CodemirrorEditor';
import ThemesEditorPreviewPane from './ThemesEditorPreviewPane';
import ThemesEditorFooter from './ThemesEditorFooter';

const props = defineProps({
    editor: {
        type: Object,
        required: true
    },
    latestCode: String,
    focused: Boolean,
});

const { proxy } = getCurrentInstance();
const store = proxy.$store;

// --- State ---

const show = ref(true);
const showPreview = ref(false);

// --- Computed ---

const alphaMode = computed(() => 0); // Preserved as per original code

// --- Methods ---

const saveEditor = () => {
    if (props.editor.isModified) {
        const settings = store.getters['themes/getSectionsSettings'](props.editor.tableName);
        const fieldName = settings.fieldName;

        const data = {
            [fieldName]: props.editor.code
        };

        store.dispatch('request/patch', {
            url: `api/component/${props.editor.tableName}/${props.editor.id}`,
            postData: data
        }).then(() => {
            store.dispatch('itemData/set', {
                tableName: props.editor.tableName,
                id: props.editor.id,
                property: fieldName,
                value: props.editor.code
            });

            // Note: Mutating props directly is generally discouraged in Vue,
            // but kept here to maintain original logic with the editor object.
            props.editor.isModified = false;
            props.editor.originalCode = props.editor.code;

            store.commit('cacheNeedsClearing');
        });
    }
};

const onKeyDown = (e) => {
    if (e.metaKey && e.keyCode === 83) { // CMD + S
        e.preventDefault();
        saveEditor();
    }
};

// --- Watchers ---

// Handle the "CMS + S" keyboard takeover when focused
watch(() => props.focused, (isFocused) => {
    if (isFocused) {
        window.addEventListener('keydown', onKeyDown);
    } else {
        window.removeEventListener('keydown', onKeyDown);
    }
}, { immediate: true });

// Force re-render of Codemirror when latestCode changes
watch(() => props.latestCode, () => {
    show.value = false;
    nextTick(() => {
        show.value = true;
    });
});

// --- Lifecycle ---

// Logic from created()
if (props.editor.tableName === 'templates_templates') {
    showPreview.value = true;
}

onBeforeUnmount(() => {
    window.removeEventListener('keydown', onKeyDown);
});
</script>

<template>
    <div>
        <div v-if="alphaMode" class="d-flex h-100">
            <main class="flex-fill w-100 overflow-hidden">
                <CodemirrorEditor v-if="editor && show" :data="editor"/>
            </main>

            <aside class="w-50">
                <ThemesEditorPreviewPane :id="editor.id" :code="editor.code" class="themes-editor-preview-pane"/>
            </aside>
        </div>

        <CodemirrorEditor v-else-if="editor && show" :data="editor" class="themes-editor--codemirror--main"/>

        <ThemesEditorFooter :editor="editor" :save-editor="saveEditor"/>
    </div>
</template>

<style scoped>
.themes-editor--codemirror--main {
    flex: auto;
    position: relative;
    height: calc(100% - 30px);
}

.themes-editor-preview-pane {
    display: block;
    height: 100%;
}

.themes-editor-preview-pane iframe {
    width: 100%;
    height: 100%;
    border: none;
}

.themes-editor-preview-pane-ajax-loader-container {
    position: relative;
    top: 50%;
    transform: translateY(-50%);
    text-align: center;
}
</style>